import { format } from "date-fns";
import { FileText, UserRound, Heart, MessageSquare, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useState } from "react";
import { FormattedClinicalText } from "./FormattedClinicalText";
import { ClinicalNote } from "./ClinicalNotes";
import { cn } from "@/lib/utils";
import { ClinicalDataLanguageNotice } from "@/components/ClinicalDataLanguageNotice";

interface ClinicalNoteModalProps {
  note: ClinicalNote | null;
  open: boolean;
  onClose: () => void;
  onAskQuestion?: (question: string, displayText?: string) => void;
}

export function ClinicalNoteModal({ note, open, onClose, onAskQuestion }: ClinicalNoteModalProps) {
  const { t } = useTranslation();
  const [selectedText, setSelectedText] = useState("");
  const [showAskPopup, setShowAskPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });

  if (!note) return null;

  const getNoteIcon = (noteType: string) => {
    switch (noteType.toLowerCase()) {
      case 'progress':
        return <UserRound className="h-5 w-5" />;
      case 'nursing':
        return <Heart className="h-5 w-5" />;
      default:
        return <FileText className="h-5 w-5" />;
    }
  };

  const getNoteTypeColor = (noteType: string) => {
    switch (noteType.toLowerCase()) {
      case 'progress':
        return 'text-primary';
      case 'nursing':
        return 'text-secondary';
      case 'consult':
        return 'text-success';
      case 'discharge':
        return 'text-accent';
      default:
        return 'text-gray-600';
    }
  };

  const handleTextSelection = (event: React.MouseEvent) => {
    const selection = window.getSelection();
    if (selection && selection.toString().trim().length > 0) {
      setSelectedText(selection.toString().trim());
      setPopupPosition({ x: event.clientX, y: event.clientY });
      setShowAskPopup(true);
    }
  };

  const handleAskAboutSelection = () => {
    if (onAskQuestion && selectedText) {
      onAskQuestion(t('explain.noteSelection', { selection: selectedText }));
    }
    setShowAskPopup(false);
    setSelectedText("");
    onClose();
  };

  const handleAskAboutNote = () => {
    if (onAskQuestion) {
      const date = format(new Date(note.createdAt), 'MMM d, yyyy');
      const fullQuestion = t('explain.noteSummaryQuestion', {
        noteType: note.noteType,
        authorName: note.authorName,
        date,
        content: note.content,
      });
      const displayText = t('explain.noteSummaryDisplay', {
        noteType: note.noteType,
        authorName: note.authorName,
        date,
      });
      onAskQuestion(fullQuestion, displayText);
    }
    onClose();
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onClose}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "max-md:bg-black/80 md:bg-black/20"
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            "flex flex-col fixed z-50 bg-white shadow-lg duration-200 h-[90vh]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            // Mobile: bottom sheet (default)
            "inset-x-0 bottom-0 border-t",
            "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
            // Desktop: center modal (override mobile styles)
            "md:inset-x-0 md:left-[50%] md:top-[50%] md:right-auto md:bottom-auto md:translate-x-[-50%] md:translate-y-[-50%] md:w-full md:max-w-3xl md:border md:bg-background",
            "md:data-[state=closed]:zoom-out-95 md:data-[state=open]:zoom-in-95",
            "md:data-[state=closed]:slide-out-to-left-1/2 md:data-[state=closed]:slide-out-to-top-[48%]",
            "md:data-[state=open]:slide-in-from-left-1/2 md:data-[state=open]:slide-in-from-top-[48%]",
            "md:rounded-lg"
          )}
        >
          {/* Header */}
          <div className="flex items-start gap-3 pb-3 border-b px-6 pt-6 flex-shrink-0">
            <div className={getNoteTypeColor(note.noteType)}>
              {getNoteIcon(note.noteType)}
            </div>
            <div className="flex-1">
              <h3 className={`text-lg font-semibold ${getNoteTypeColor(note.noteType)}`}>
                {note.noteType}
              </h3>
              <div className="text-sm text-text-secondary mt-1">
                {note.authorName} • {format(new Date(note.createdAt), "MMM d, yyyy 'at' h:mm a")}
              </div>
              {note.authorTitle && (
                <div className="text-xs text-text-secondary mt-0.5">
                  {note.authorTitle}
                </div>
              )}
            </div>
          </div>

          {/* Content - Scrollable */}
          <div
            className="text-text-primary px-6 py-4 flex-1 pointer-events-auto overflow-y-auto"
            onMouseUp={handleTextSelection}
            style={{ minHeight: 0, WebkitOverflowScrolling: 'touch' }}
            tabIndex={0}
          >
            <ClinicalDataLanguageNotice />
            <FormattedClinicalText text={note.content} />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-3 border-t px-6 pb-6 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
            >
              {t('records.close')}
            </Button>
            <Button
              size="sm"
              onClick={handleAskAboutNote}
              className="gap-1.5"
            >
              <MessageSquare className="h-3.5 w-3.5" />
              {t('explain.buttonNoteLabel')}
            </Button>
          </div>

          {/* Text selection popup */}
          {showAskPopup && (
            <div
              className="fixed z-[100] bg-white border border-gray-200 rounded-lg shadow-lg p-2"
              style={{ left: popupPosition.x, top: popupPosition.y - 40 }}
              onMouseLeave={() => setShowAskPopup(false)}
            >
              <Button
                variant="ghost"
                size="sm"
                onClick={handleAskAboutSelection}
                className="text-blue-600 hover:bg-blue-50"
              >
                <MessageSquare className="h-3 w-3 mr-1" />
                {t('records.askAboutThis')}
              </Button>
            </div>
          )}

          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
            <X className="h-4 w-4" />
            <span className="sr-only">{t('records.close')}</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
// Force reload Sat Jan  3 16:00:03 EST 2026
